import React, { useEffect, useRef, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAgencyContext } from '../contexts/AppContexts';
import { processImageFile, IMAGE_CONFIG } from '../utils/imageCompression';
import useImageDrop from '../utils/useImageDrop';
import { ArrowLeft, Camera, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${API_BASE_URL}/api`;

const ResourceForm = ({ onBack, onSave, editingResource, positions = [], exerciseId = null }) => {
  const { selectedAgency } = useAgencyContext();
  const [formData, setFormData] = useState({
    resource_type: '',
    order_request_no: '',
    incident_id: '',
    vehicle_equipment_make_model: '',
    features: '',
    request: 0,
    quantity_available: 0,
    quantity_needed: 0,
    contact_person: '',
    contact_phone: '',
    resource_image: null,
    assigned_exercise: exerciseId || '',
    agency: '',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [exercises, setExercises] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectKey, setSelectKey] = useState(0);

  // Refs for required fields (auto-scroll on validation failure)
  const fieldRefs = {
    resource_type: useRef(null),
    quantity_available: useRef(null),
    quantity_needed: useRef(null),
    contact_person: useRef(null),
    contact_phone: useRef(null),
  };
  const requiredFieldOrder = ['resource_type', 'quantity_available', 'quantity_needed', 'contact_person', 'contact_phone'];

  // Fetch all exercises
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        let url = `${API_BASE_URL}/api/exercise-builder`;
        if (selectedAgency) {
          url += `?organization_id=${selectedAgency.id}`;
        }
        const response = await fetch(url, { headers });
        if (response.ok) {
          const data = await response.json();
          setExercises(data || []);
        }
      } catch (error) {
        console.error('Error fetching exercises:', error);
      }
    };
    fetchExercises();
  }, [selectedAgency]);

  // Fetch organizations from the exercise's Organization Coordination list (Step 8)
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!exerciseId) return;
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/exercise-builder/${exerciseId}`, { headers });
        const orgs = Array.isArray(response.data?.organizations) ? response.data.organizations : [];
        const names = orgs.map(o => o.name).filter(Boolean);
        setAgencies(names);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, [exerciseId]);

  // Resource type options
  const resourceTypes = [
    'Fire: Pumper',
    'Fire: Ladder',
    'Fire: Rescue',
    'Fire: Tanker',
    'Fire: Command',
    'Ambulance: Type 1 Box',
    'Ambulance: Type 2 Van',
    'Ambulance: Type 3 Box',
    'Ambulance: Rapid Response',
    'Ambulance: Multi Patient Transfer',
    'Ambulance: Bus',
    'Police: Car',
    'Police: Command',
    'Police: Tactical',
    'Emerg Response: Command Post',
    'Emerg Response: Communications',
    'Emerg Response: Utility',
    'Support Light/Duty: Pickup Truck',
    'Support Light/Duty: 4WD Vehicle',
    'Support Light/Duty: SUV',
    'Salvation: Food/Shelter Support',
    'Salvation: Mobile Kitchens',
    'Salvation: Response Unit',
    'Salvation: Weather/Food/Shelters',
    'Salvation: Shower Trailers',
    'Salvation: Multi-Purpose Support Vans',
    'Red Cross: Van/Bus/Triage',
    'Heavy/Special: Grater',
    'Heavy/Special: Dozer',
    'Heavy/Special: Snow Plow',
    'Heavy/Special: Backhoe',
    'Heavy/Special: Water Tender',
    'Heavy/Special: Auger',
    'Heavy/Special: Bucket',
    'Heavy/Special: Utility',
    'Heavy/Special: Crane',
    'Heavy/Special: Tow Truck',
    'Heavy/Special: Hydro/Power Utility',
    'Military: Transport',
    'Military: Auger',
    'Military: Utility',
    'Military: LAV support',
    'Support: Generator',
    'Support: Lighting Unit',
    'Other:'
  ];

  useEffect(() => {
    if (editingResource) {
      setFormData({
        resource_type: editingResource.resource_type || '',
        order_request_no: editingResource.order_request_no || '',
        incident_id: editingResource.incident_id || '',
        vehicle_equipment_make_model: editingResource.vehicle_equipment_make_model || '',
        features: editingResource.features || '',
        request: editingResource.request || 0,
        quantity_available: editingResource.quantity_available || 0,
        quantity_needed: editingResource.quantity_needed || 0,
        contact_person: editingResource.contact_person || '',
        contact_phone: editingResource.contact_phone || '',
        resource_image: editingResource.resource_image || null,
        assigned_exercise: editingResource.assigned_exercise || '',
        agency: editingResource.agency || '',
      });

      // Resolve assigned_exercise name to UUID if needed
      const storedExercise = editingResource.assigned_exercise || '';
      if (storedExercise && exercises.length > 0) {
        const matchById = exercises.find(e => e.id === storedExercise);
        if (!matchById) {
          const matchByName = exercises.find(e => e.exercise_name === storedExercise);
          if (matchByName) {
            setFormData(prev => ({ ...prev, assigned_exercise: matchByName.id }));
          }
        }
      }

      setSelectKey(prev => prev + 1);

      if (editingResource.resource_image) {
        setImagePreview(editingResource.resource_image);
      }
    } else {
      setFormData({
        resource_type: '',
        order_request_no: '',
        incident_id: '',
        vehicle_equipment_make_model: '',
        features: '',
        request: 0,
        quantity_available: 0,
        quantity_needed: 0,
        contact_person: '',
        contact_phone: '',
        resource_image: null,
        assigned_exercise: exerciseId || '',
        agency: '',
      });
      setImagePreview(null);
    }
  }, [editingResource, exerciseId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.resource_type.trim()) {
      newErrors.resource_type = 'Resource type is required';
    }

    if (formData.quantity_available < 0) {
      newErrors.quantity_available = 'Quantity available cannot be negative';
    }

    if (formData.quantity_needed < 0) {
      newErrors.quantity_needed = 'Quantity needed cannot be negative';
    }

    if (!formData.contact_person.trim()) {
      newErrors.contact_person = 'Contact person is required';
    }

    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Contact phone is required';
    } else if (!/^\d{3}-\d{3}-\d{4}$/.test(formData.contact_phone)) {
      newErrors.contact_phone = 'Phone must be in format: 123-456-7890';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const result = await processImageFile(file, 'resource');
      if (result.success) {
        setImagePreview(result.data);
        setFormData(prev => ({ ...prev, resource_image: result.data }));
      } else {
        alert(result.error || 'Failed to process image');
      }
    }
  };

  const { dropProps: resourceDropProps, isDragging: isResourceDragging } = useImageDrop({
    onFile: (file) => handleImageUpload({ target: { files: [file] } })
  });

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        const config = IMAGE_CONFIG.resource;
        const scale = Math.min(config.maxWidth / video.videoWidth, config.maxHeight / video.videoHeight, 1);
        canvas.width = video.videoWidth * scale;
        canvas.height = video.videoHeight * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = canvas.toDataURL('image/jpeg', config.quality);
        setImagePreview(imageData);
        setFormData(prev => ({ ...prev, resource_image: imageData }));

        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      for (const field of requiredFieldOrder) {
        if (validationErrors[field] && fieldRefs[field]?.current) {
          fieldRefs[field].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusable = fieldRefs[field].current.querySelector('input, textarea, button, select');
          if (focusable) {
            setTimeout(() => focusable.focus(), 400);
          }
          break;
        }
      }
      return;
    }

    setLoading(true);

    try {
      const resourceData = {
        resource_type: formData.resource_type,
        order_request_no: formData.order_request_no,
        incident_id: formData.incident_id,
        vehicle_equipment_make_model: formData.vehicle_equipment_make_model,
        features: formData.features,
        request: parseInt(formData.request) || 0,
        quantity_available: parseInt(formData.quantity_available),
        quantity_needed: parseInt(formData.quantity_needed),
        contact_person: formData.contact_person,
        contact_phone: formData.contact_phone,
        resource_image: formData.resource_image,
        assigned_exercise: formData.assigned_exercise,
        agency: formData.agency,
      };

      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingResource) {
        const response = await axios.put(`${API}/resources/${editingResource.id}`, resourceData, { headers });
        onSave(response.data);
      } else {
        const response = await axios.post(`${API}/resources`, resourceData, { headers });
        onSave(response.data);
      }
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error saving resource. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Phone number formatting
  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, '');
    let formatted = digits;
    if (digits.length >= 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    handleInputChange('contact_phone', formatted);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">
            {editingResource ? 'Edit Resource' : 'Add New Resource'}
          </h1>
          <p className="text-gray-400">
            {editingResource ? 'Update resource information' : 'Enter details for the new resource'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={onBack}
          className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Resources
        </Button>
      </div>

      <Card className="bg-gray-900 border-gray-700">
        <CardContent className="p-6">
          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Resource Image Upload */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-orange-500">Resource Image</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-6">
                  <div
                    {...resourceDropProps}
                    onClick={() => document.getElementById('resource-image-upload').click()}
                    data-testid="resource-image-dropzone"
                    title="Click to browse or drop an image here"
                    className={`w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                      isResourceDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
                    }`}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Resource" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center px-2">
                        <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <span className="text-xs text-gray-400">{isResourceDragging ? 'Drop to upload' : 'No image · Drop here'}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="resource-image-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('resource-image-upload').click()}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 mr-3"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Image
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCameraCapture}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Use Camera
                      </Button>
                    </div>
                    <p className="text-sm text-gray-400">
                      Add a photo of the resource for easier identification
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resource Type */}
            <div ref={fieldRefs.resource_type}>
              <Label htmlFor="resource_type" className="text-white">
                Resource Type <span className="text-red-500">*</span>
              </Label>
              <Select
                key={`resource-type-${editingResource ? editingResource.id : 'new'}-${formData.resource_type}`}
                value={formData.resource_type || ''}
                onValueChange={(value) => handleInputChange('resource_type', value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select resource type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {resourceTypes.map((type) => (
                    <SelectItem key={type} value={type} className="text-white hover:bg-gray-700">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.resource_type && (
                <p className="text-red-400 text-sm mt-1">{errors.resource_type}</p>
              )}
            </div>

            {/* Additional Resource Information */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
              <div className="flex flex-col">
                <Label htmlFor="order_request_no" className="text-white mb-0">
                  Order Request No.
                </Label>
                <Input
                  id="order_request_no"
                  type="text"
                  value={formData.order_request_no}
                  onChange={(e) => handleInputChange('order_request_no', e.target.value)}
                  placeholder="Order request number"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 mt-0"
                />
              </div>

              <div className="flex flex-col">
                <Label htmlFor="incident_id" className="text-white mb-0">
                  Incident ID #
                </Label>
                <Input
                  id="incident_id"
                  type="text"
                  value={formData.incident_id}
                  onChange={(e) => handleInputChange('incident_id', e.target.value)}
                  placeholder="Incident ID number"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 mt-0"
                />
              </div>

              <div className="flex flex-col">
                <Label htmlFor="vehicle_equipment_make_model" className="text-white mb-0">
                  Make / Model
                </Label>
                <Input
                  id="vehicle_equipment_make_model"
                  type="text"
                  value={formData.vehicle_equipment_make_model}
                  onChange={(e) => handleInputChange('vehicle_equipment_make_model', e.target.value)}
                  placeholder="Make and model"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 mt-0"
                />
              </div>

              <div className="flex flex-col">
                <Label htmlFor="features" className="text-white mb-0">
                  Features
                </Label>
                <Input
                  id="features"
                  type="text"
                  value={formData.features}
                  onChange={(e) => handleInputChange('features', e.target.value)}
                  placeholder="Key features"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 mt-0"
                />
              </div>
            </div>

            {/* Quantities */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="request" className="text-white">
                  Request
                </Label>
                <Input
                  id="request"
                  type="number"
                  min="0"
                  value={formData.request}
                  onChange={(e) => handleInputChange('request', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div ref={fieldRefs.quantity_available}>
                <Label htmlFor="quantity_available" className="text-white">
                  Have <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="quantity_available"
                  type="number"
                  min="0"
                  value={formData.quantity_available}
                  onChange={(e) => handleInputChange('quantity_available', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {errors.quantity_available && (
                  <p className="text-red-400 text-sm mt-1">{errors.quantity_available}</p>
                )}
              </div>

              <div ref={fieldRefs.quantity_needed}>
                <Label htmlFor="quantity_needed" className="text-white">
                  Need <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="quantity_needed"
                  type="number"
                  min="0"
                  value={formData.quantity_needed}
                  onChange={(e) => handleInputChange('quantity_needed', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {errors.quantity_needed && (
                  <p className="text-red-400 text-sm mt-1">{errors.quantity_needed}</p>
                )}
              </div>
            </div>

            {/* Assigned Exercise */}
            <div>
              <Label htmlFor="assigned_exercise" className="text-white">
                Assigned Exercise
              </Label>
              <Select
                key={`assigned-exercise-select-${selectKey}`}
                value={formData.assigned_exercise || 'none'}
                onValueChange={(value) => handleInputChange('assigned_exercise', value === 'none' ? '' : value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select exercise" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                  <SelectItem value="none" className="text-white hover:bg-gray-700">
                    None
                  </SelectItem>
                  {exercises.map((exercise) => (
                    <SelectItem key={exercise.id} value={exercise.id} className="text-white hover:bg-gray-700">
                      {exercise.exercise_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div ref={fieldRefs.contact_person}>
                <Label htmlFor="contact_person" className="text-white">
                  Contact Person <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact_person"
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  placeholder="Primary contact person name"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
                {errors.contact_person && (
                  <p className="text-red-400 text-sm mt-1">{errors.contact_person}</p>
                )}
              </div>

              <div ref={fieldRefs.contact_phone}>
                <Label htmlFor="contact_phone" className="text-white">
                  Contact Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="123-456-7890"
                  maxLength="12"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
                {errors.contact_phone && (
                  <p className="text-red-400 text-sm mt-1">{errors.contact_phone}</p>
                )}
              </div>
            </div>

            {/* Organization(s) */}
            <div>
              <Label htmlFor="agency" className="text-white">
                Organization(s)
              </Label>
              <Select
                key={`agency-select-${selectKey}`}
                value={formData.agency || 'none'}
                onValueChange={(value) => handleInputChange('agency', value === 'none' ? '' : value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                  <SelectItem value="none" className="text-white hover:bg-gray-700">
                    None
                  </SelectItem>
                  {agencies.length === 0 && (
                    <SelectItem value="__no_orgs__" disabled className="text-gray-500 italic">
                      No organizations linked to this exercise
                    </SelectItem>
                  )}
                  {agencies.map((agencyName) => (
                    <SelectItem key={agencyName} value={agencyName} className="text-white hover:bg-gray-700">
                      {agencyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="text-gray-400 border-gray-600 hover:bg-gray-800 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
              >
                {loading ? 'Saving...' : (editingResource ? 'Update Resource' : 'Add Resource')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourceForm;
